import {
  CSSProperties,
  ElementType,
  PropsWithChildren,
  useEffect,
  useRef,
  useState,
} from 'react';

import { Badge, Fade, Theme, makeStyles, styled } from '@material-ui/core';

import classNames from 'classnames';

import { CommentZoneProvider, useZone } from '../../contexts';
import { useCommentState, useOpenCommentsForZone } from '../../hooks';
import { isAnchorEqual } from '../../types';
import { useDelayedTrigger } from './useDelayedTrigger';

const Highlight = styled('span')(({ theme }) => ({
  position: 'absolute',
  left: -theme.spacing(1),
  top: -theme.spacing(0.5),
  bottom: -theme.spacing(0.5),
  right: -theme.spacing(1),
  opacity: 0.5,
  background: theme.palette.warning.dark,
  border: `3px solid ${theme.palette.warning.light}`,
  borderRadius: theme.shape.borderRadius,
  zIndex: theme.zIndex.appBar - 1,
  cursor: 'cell',
}));

const useStyles = makeStyles<
  Theme,
  {
    threadHasContent: boolean;
    display?: React.CSSProperties['display'];
    preserveBadgeSpace?: boolean;
  }
>(theme => ({
  root: {
    position: 'relative',
    textOverflow: 'inherit',
    whiteSpace: 'inherit',
    overflow: 'inherit',
  },
  threadBadge: {
    zIndex: theme.zIndex.appBar - 1,
    cursor: ({ threadHasContent }) =>
      threadHasContent ? 'pointer' : 'default',
    display: ({ display }) => display || 'block',
    position: 'relative',
    textOverflow: 'inherit',
    whiteSpace: 'inherit',
    overflow: 'inherit',
    '& > [class*="MuiBadge-badge"]': {
      transform: ({ preserveBadgeSpace }) => {
        if (preserveBadgeSpace === undefined) return undefined;
        return preserveBadgeSpace ? 'scale(1) translate(0, 0)' : 'scale(0)';
      },
    },
  },
}));

type CommentZoneProps<T extends ElementType = 'div'> = PropsWithChildren<{
  id: string;
  data?: { [key: string]: string };
  component?: T;
  display?: CSSProperties['display'];
  className?: string;
  preserveBadgeSpace?: boolean;
}>;

type InternalProps<T extends ElementType = 'div'> = CommentZoneProps<T> & {
  zone: string;
};

const CommentZoneInert = <T extends ElementType = 'div'>({
  children,
  component,
  className,
  zone,
}: InternalProps<T>) => {
  const Component = component || 'div';
  return (
    <CommentZoneProvider zone={zone}>
      <Component className={className}>{children}</Component>
    </CommentZoneProvider>
  );
};

const CommentZoneActive = <T extends ElementType = 'div'>({
  children,
  component,
  display,
  className,
  zone,
  preserveBadgeSpace,
}: InternalProps<T>) => {
  const Component = component || 'div';
  const selectedAnchor = useCommentState(state => state.selectedAnchor);
  const comment = useCommentState(state => state.comment);
  const hoveredAnchor = useCommentState(state => state.hoveredAnchor);

  const selectComment = useCommentState(state => state.selectComment);
  const commentMode = useCommentState(state => state.commentMode);
  const commentsEnabled = useCommentState(state => state.commentsEnabled);
  const readOnly = useCommentState(state => state.readOnly);

  const [hover, setHover] = useState(false);
  const ref = useRef<React.ComponentPropsWithRef<T>['ref']>(null);

  const isHovered = hoveredAnchor && isAnchorEqual(hoveredAnchor, { zone });
  const isSelected = selectedAnchor && isAnchorEqual(selectedAnchor, { zone });

  const comments = useOpenCommentsForZone(zone);
  const commentCount = comments.length;
  const classes = useStyles({
    threadHasContent: commentCount > 0,
    display,
    preserveBadgeSpace: preserveBadgeSpace && commentCount > 0,
  });

  useEffect(() => {
    if (isSelected && ref.current) {
      ref.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [isSelected, zone]);

  const { value: flash, trigger: triggerFlash } = useDelayedTrigger({
    delay: 350,
    duration: 250,
  });

  const enteredCommentMode = commentsEnabled && commentMode && !selectedAnchor;

  useEffect(() => {
    if (enteredCommentMode) {
      triggerFlash();
    }
  }, [enteredCommentMode, triggerFlash]);

  return (
    <CommentZoneProvider zone={zone}>
      <Component
        ref={ref}
        className={classNames(classes.root, className)}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
      >
        {!readOnly && (
          <Fade in={flash} appear timeout={300} mountOnEnter unmountOnExit>
            <Highlight />
          </Fade>
        )}

        {(isSelected || isHovered) && <Highlight />}

        <Badge
          className={classes.threadBadge}
          onClick={() =>
            commentCount ? selectComment(comments[0].name, { zone }) : null
          }
          color="primary"
          variant="standard"
          overlap="rectangular"
          badgeContent={commentCount}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          {children}
        </Badge>

        {hover && commentsEnabled && commentMode && !readOnly && (
          <Highlight
            onClick={e => {
              comment({ zone });
              e.preventDefault();
            }}
          />
        )}
      </Component>
    </CommentZoneProvider>
  );
};

export const CommentZone = <T extends ElementType = 'div'>(
  props: CommentZoneProps<T>,
) => {
  const commentMode = useCommentState(state => state.commentMode);
  const hasComments = useCommentState(state => state.comments.length > 0);
  const zone = useZone(props.id, props.data);

  if (!commentMode && !hasComments) {
    return <CommentZoneInert<T> {...props} zone={zone} />;
  }
  return <CommentZoneActive<T> {...props} zone={zone} />;
};

export const createCommentZone = <T extends ElementType = 'div'>(
  options: Omit<CommentZoneProps<T>, 'children'>,
) => {
  const Wrapper = ({ children }: PropsWithChildren) => (
    <CommentZone<T> {...options}>{children}</CommentZone>
  );
  Wrapper.displayName = `CommentZone(${options.id})`;
  return Wrapper;
};
